import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '../../../shared/auth/user.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  registerForm: FormGroup;
  loading = false;
  hidePassword = true;
  hideConfirmPassword = true;
  passwordStrength = 0;
  passwordRequirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private userService: UserService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.registerForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    }, { validator: this.passwordMatchValidator });

    // Check password strength on value changes
    this.registerForm.get('password').valueChanges.subscribe(password => {
      this.checkPasswordStrength(password);
    });
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password').value;
    const confirmPassword = control.get('confirmPassword').value;
    
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  checkPasswordStrength(password: string): void {
    if (!password) {
      this.passwordStrength = 0;
      this.passwordRequirements = {
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false
      };
      return;
    }

    this.passwordRequirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password)
    };

    // Calculate strength score (0-5)
    this.passwordStrength = Object.values(this.passwordRequirements).filter(Boolean).length;
  }

  getPasswordStrengthText(): string {
    switch (this.passwordStrength) {
      case 0:
        return 'Very Weak';
      case 1:
        return 'Weak';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Strong';
      case 5:
        return 'Very Strong';
      default:
        return 'Very Weak';
    }
  }

  getErrorMessage(field: string): string {
    const control = this.registerForm.get(field);
    
    if (!control || !control.errors) {
      return '';
    }
    
    if (control.errors.required) {
      return `${this.capitalizeFirstLetter(field)} is required`;
    }
    
    if (field === 'email' && control.errors.email) {
      return 'Please enter a valid email address';
    }
    
    if (field === 'username' && control.errors.minlength) {
      return 'Username must be at least 3 characters long';
    }
    
    if (field === 'password' && control.errors.minlength) {
      return 'Password must be at least 8 characters long';
    }
    
    return '';
  }

  onSubmit(): void {
    if (this.registerForm.invalid || this.loading) {
      return;
    }
    
    this.loading = true;
    
    const { username, firstName, lastName, email, password } = this.registerForm.value;
    
    this.userService.registerUser(username, firstName, lastName, email, password)
      .subscribe(
        response => {
          this.loading = false;
          this.snackBar.open('Registration successful! Please log in.', 'Close', { duration: 5000 });
          this.navigateToLogin();
        },
        error => {
          this.loading = false;
          this.snackBar.open(error.error?.message || 'Registration failed. Please try again.', 'Close', { duration: 5000 });
        }
      );
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  private capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
} 